import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { UseMutationResult } from '@tanstack/react-query';
import { useDisclosure } from '@ds';
import { retrieveUnifiedError } from '@/api/utils';

type UseEntityDeleteType<T = undefined> = {
  isOpen: boolean;
  entityId: string | null;
  entityData: T | null;
  isLoading: boolean;
  onModalOpen: T extends undefined ? (id: string) => void : (id: string, data: T) => void;
  onModalClose: () => void;
  onEntityDelete: () => Promise<void>;
};

type UseEntityDeleteProps = {
  successMessage: string;
  query: () => UseMutationResult<
    { correlationId?: string; message: string | null },
    Error,
    { id: string; type?: string }
  >;
  type?: string;
  onDeleteCb?: () => void;
};

export const useEntityDelete = <T = undefined>({
  successMessage,
  query,
  type,
  onDeleteCb,
}: UseEntityDeleteProps): UseEntityDeleteType<T> => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { mutateAsync } = query();
  const [entityId, setEntityId] = useState<string | null>(null);
  const [entityData, setEntityData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onModalOpen = useCallback(
    (id: string, data?: T) => {
      setEntityId(id);
      if (data) setEntityData(data);
      onOpen();
    },
    [onOpen],
  );

  const onModalClose = useCallback(() => {
    setEntityId(null);
    setEntityData(null);
    onClose();
  }, [onClose]);

  const onEntityDelete = useCallback(async () => {
    if (!entityId) return;

    try {
      setIsLoading(true);
      await mutateAsync({ id: entityId, type });
      toast.success(successMessage);
      onClose();
      onDeleteCb?.();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [type, successMessage, onDeleteCb, mutateAsync, entityId, onClose]);

  return {
    isOpen,
    entityId,
    entityData,
    isLoading,
    onModalOpen,
    onModalClose,
    onEntityDelete,
  } as UseEntityDeleteType<T>;
};
